import { Check, MagnifyingGlass, Robot } from "@phosphor-icons/react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { AvailableThinker } from "~/api/aiAdminTypes";
import { useAvailableThinkers, useSuggestedThinkers } from "~/api/queries";
import { Pill } from "~/features/automation/components";
import { Avatar } from "~/foundation/components/Avatar";
import { Button } from "~/foundation/components/Button";
import { Dialog } from "~/foundation/components/Dialog";
import { Empty } from "~/foundation/components/Empty";
import { Input } from "~/foundation/components/Input";
import { Spinner } from "~/foundation/components/Spinner";
import { Text } from "~/foundation/components/Text";
import { cn } from "~/foundation/utils/cn";

/**
 * Which thinkers the picker will offer. The default is everything the workspace can address,
 * which is what a conversation participant picker wants; a flow node inspector narrows it.
 */
export type ThinkerFilter = "all" | "ai" | "human" | "local";

export interface ThinkerPickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Ids already chosen. The picker is controlled — it never holds the answer. */
  selected: string[];
  onSelect: (ids: string[]) => void;
  multiple?: boolean;
  filter?: ThinkerFilter;
  /**
   * When given, the people whose topic areas match this document are offered first, under
   * their own heading. This is the whole point of tagging an assistant.
   */
  documentId?: string;
  title?: string;
  /** Text for the confirm button, when the caller wants something other than "Choose". */
  confirmLabel?: string;
}

function matches(thinker: AvailableThinker, filter: ThinkerFilter): boolean {
  switch (filter) {
    case "ai":
      return !thinker.isHuman;
    case "human":
      return thinker.isHuman;
    case "local":
      return thinker.isLocal;
    default:
      return true;
  }
}

/** One row. A thinker the server said is not runnable is shown and cannot be picked. */
function ThinkerRow({
  thinker,
  selected,
  onToggle,
}: {
  thinker: AvailableThinker;
  selected: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation();

  return (
    <button
      type="button"
      disabled={!thinker.usable}
      onClick={onToggle}
      aria-pressed={selected}
      className={cn(
        "flex w-full items-center gap-3 rounded-sm px-2 py-2 text-start",
        thinker.usable ? "cursor-pointer hover:bg-hover-bg" : "cursor-not-allowed opacity-60",
        selected && "bg-menu-item-selected",
      )}
    >
      <Avatar name={thinker.name} src={thinker.avatarUrl} size={28} />
      <div className="min-w-0 flex-1">
        <p className="truncate text-body text-text">{thinker.name}</p>
        <p className="truncate text-small text-text-tertiary">{thinker.description}</p>
      </div>
      {thinker.isLocal ? <Pill>{t("Local")}</Pill> : null}
      {thinker.isHuman ? <Pill>{t("Person")}</Pill> : null}
      {thinker.kind === "ide" ? <Pill tone="accent">{t("IDE")}</Pill> : null}
      {/* Not a silent omission: the workspace can see it, and cannot run it. */}
      {thinker.usable ? null : <Pill tone="warning">{t("View only")}</Pill>}
      {selected ? <Check size={16} weight="bold" aria-hidden className="text-accent" /> : null}
    </button>
  );
}

/**
 * Choose a thinker — the one picker every AI surface uses.
 *
 * It sources from `GET /api/thinkers`, which is the server's own runnable set, so "it is in
 * the picker" and "it can actually be run" cannot disagree. Entries the server marked
 * `usable:false` (a persona shared view-only) are listed and disabled rather than hidden:
 * a missing row reads as a bug, and an offered row that fails at send-time is worse.
 */
export function ThinkerPicker({
  open,
  onOpenChange,
  selected,
  onSelect,
  multiple = false,
  filter = "all",
  documentId,
  title,
  confirmLabel,
}: ThinkerPickerProps) {
  const { t } = useTranslation();
  const { data: available, isLoading } = useAvailableThinkers();
  const { data: suggested } = useSuggestedThinkers(documentId ?? null);
  const [search, setSearch] = useState("");
  const [draft, setDraft] = useState<string[]>(selected);

  const query = search.trim().toLowerCase();
  const suggestedIds = useMemo(
    () => new Set((suggested ?? []).map((thinker) => thinker.id)),
    [suggested],
  );

  const rows = (available ?? [])
    .filter((thinker) => matches(thinker, filter))
    .filter(
      (thinker) =>
        !query ||
        thinker.name.toLowerCase().includes(query) ||
        thinker.description.toLowerCase().includes(query),
    );
  const highlighted = rows.filter((thinker) => suggestedIds.has(thinker.id));
  const rest = rows.filter((thinker) => !suggestedIds.has(thinker.id));

  const toggle = (id: string) => {
    if (!multiple) {
      setDraft([id]);
      return;
    }
    setDraft(draft.includes(id) ? draft.filter((entry) => entry !== id) : [...draft, id]);
  };

  const confirm = () => {
    onSelect(draft);
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (next) {
          // Re-opening starts from the caller's truth, not from an abandoned draft.
          setDraft(selected);
          setSearch("");
        }
        onOpenChange(next);
      }}
      title={title ?? t("Choose a thinker")}
      closeLabel={t("Close")}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t("Cancel")}
          </Button>
          <Button variant="primary" disabled={draft.length === 0} onClick={confirm}>
            {confirmLabel ?? t("Choose")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Input
          type="search"
          value={search}
          fullWidth
          placeholder={t("Search thinkers")}
          onChange={(event) => setSearch(event.target.value)}
        />

        {isLoading ? (
          <Spinner label={t("Loading")} />
        ) : rows.length === 0 ? (
          <Empty
            icon={query ? MagnifyingGlass : Robot}
            message={
              query
                ? t("No thinker matches that search.")
                : t("This workspace has not added any thinkers yet.")
            }
          />
        ) : (
          <div className="flex max-h-96 flex-col gap-3 overflow-y-auto">
            {highlighted.length > 0 ? (
              <div className="flex flex-col gap-0.5">
                <Text variant="small">{t("Suggested for this document")}</Text>
                {highlighted.map((thinker) => (
                  <ThinkerRow
                    key={thinker.id}
                    thinker={thinker}
                    selected={draft.includes(thinker.id)}
                    onToggle={() => toggle(thinker.id)}
                  />
                ))}
              </div>
            ) : null}

            <div className="flex flex-col gap-0.5">
              {highlighted.length > 0 ? <Text variant="small">{t("Everyone else")}</Text> : null}
              {rest.map((thinker) => (
                <ThinkerRow
                  key={thinker.id}
                  thinker={thinker}
                  selected={draft.includes(thinker.id)}
                  onToggle={() => toggle(thinker.id)}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </Dialog>
  );
}
